import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import type { TenantResponse } from '@/api/responses'
import { Alert, Button, Input } from '@/components/ui'
import { usePublicPlans } from '@/features/billing/queries'
import { createTenant } from '@/features/tenants/api'

const MIN_NAME_LENGTH = 2

export interface OrganizationStepProps {
  readonly onSuccess: (tenant: TenantResponse) => void
}

/**
 * Passo 1 da wizard: cria a organizacao (`POST /api/v1/tenants`). Captura o nome e o site da empresa
 * (opcional) — o site alimentara a Descoberta Automatica de Conhecimento (SS-02). Nenhum campo
 * decorativo que nao faca nada (CLAUDE.md — sem dado falso).
 */
export function OrganizationStep({ onSuccess }: OrganizationStepProps) {
  const { t } = useTranslation()
  const plans = usePublicPlans()
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const mutation = useMutation({
    mutationFn: createTenant,
    onSuccess,
  })

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()

    if (name.trim().length < MIN_NAME_LENGTH) {
      setNameError(t('onboarding.createOrganization.errors.nameLength'))
      return
    }

    setNameError(undefined)
    const trimmedWebsite = website.trim()
    mutation.mutate({
      name: name.trim(),
      ...(trimmedWebsite.length > 0 ? { website: trimmedWebsite } : {}),
    })
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <div className="grid gap-10 p-6 sm:grid-cols-2 sm:p-10">
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-action-primary">
          {t('onboarding.createOrganization.eyebrow')}
        </span>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground-primary sm:text-3xl">
          {t('onboarding.createOrganization.heading')}
        </h2>
        <p className="text-sm leading-relaxed text-foreground-secondary">
          {t('onboarding.createOrganization.subtitle')}
        </p>
        {plans.data !== undefined && plans.data.trial.credits > 0 ? (
          <p className="text-sm font-medium text-foreground-primary" data-testid="trial-copy">
            {t('billing.onboarding.organization', {
              credits: plans.data.trial.credits,
              days: plans.data.trial.days,
            })}
          </p>
        ) : null}
      </div>
      <form
        onSubmit={(event) => {
          handleSubmit(event)
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        {firstMessage !== undefined ? (
          <Alert variant="danger" title={firstMessage}>
            {restMessages.length > 0 ? (
              <ul className="list-disc pl-4">
                {restMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            ) : null}
          </Alert>
        ) : null}
        <Input
          id="organization-name"
          type="text"
          label={t('onboarding.createOrganization.nameLabel')}
          autoComplete="organization"
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          error={nameError}
        />
        <Input
          id="organization-website"
          type="text"
          inputMode="url"
          label={t('onboarding.createOrganization.websiteLabel')}
          hint={t('onboarding.createOrganization.websiteHint')}
          placeholder={t('onboarding.createOrganization.websitePlaceholder')}
          autoComplete="url"
          value={website}
          onChange={(event) => {
            setWebsite(event.target.value)
          }}
        />
        <Button type="submit" loading={mutation.isPending} className="self-start">
          {mutation.isPending
            ? t('onboarding.createOrganization.submitting')
            : t('onboarding.createOrganization.submit')}
        </Button>
      </form>
    </div>
  )
}
